import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import device from 'current-device';
import * as Icons from '@seismic/icons';
import { useLoadDownloadAssets, useLoadWatermarkManifest } from '../commonUtil/hookUtil';
import Header from '../components/header';
import LinkViewer from '../components/linkViewer';
import { withLinkStore, ACTION_TYPES_CHANGE_CONTENT, ACTION_TYPES_SHOW_SHARE_MODAL, ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL } from '../app/reducers';
import CompliancePage from './compliancePage';
import Notification from './common/notification';
import { isPresentationMode } from '../commonUtil/presentationUtil';
import ErrorModal from './common/errorModal';
import { SHARE_RESULT_CODE } from '../commonUtil/constants';
import UxEventService from '../service/UxEventService';
import { UXContextType } from '../commonUtil/util';
import ShareModal from './shareModal';
import ApplicationEntity from '../commonUtil/appEntity';
import MicroAppPagesCollection from '../commonUtil/microappView';
import ContentType from '../model/contentType';

const ViewerContainer = (props) => {
    const { selectedContent, isLoadingManifest, selectedManifest, contents, hasCustomFooter, fromInitialDoc, showShareModal, viewerSetting } = props.linkStore;
    const [validPasscode, setPasscodeValidation] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [shareSuccess, setShareSuccess] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [errorInfo, setErrorInfo] = useState({});

    useLoadWatermarkManifest();
    useLoadDownloadAssets();

    const shouldOpenPanel = () => {
        if (contents.length > 1) {
            if (!fromInitialDoc) {
                return true;
            }

            if (fromInitialDoc && device.desktop()) {
                return true;
            }
        }

        if (isPresentationMode()) {
            return true;
        }

        return false;
    };

    const [openPanel, setOpenPanel] = useState(shouldOpenPanel());
    const { t } = useTranslation();

    const handleSelectContent = (content) => {
        ApplicationEntity.getInstance().selectedContentId = content.id;
        props.linkDispatch({ type: ACTION_TYPES_CHANGE_CONTENT, payload: { selectedContentId: content.id } });
        if (content.format === ContentType.MICROAPP) {
            MicroAppPagesCollection.getInstance().selectedContentVersionId = content.versionId;
            MicroAppPagesCollection.getInstance().isMicroApp = true;
        } else {
            MicroAppPagesCollection.getInstance().isMicroApp = false;
        }
    };

    const setIsOpenShareModal = (value) => {
        props.linkDispatch({ type: ACTION_TYPES_SHOW_SHARE_MODAL, payload: value });
    };

    const onShareSuccess = () => {
        setShowNotification(true);
        setShareSuccess(true);
        setIsOpenShareModal(false);
    };

    const onShareFailed = (shareResult, shareInfos) => {
        if (shareResult.code === SHARE_RESULT_CODE.ProhibitedRecipients) {
            const recipient = shareInfos.newRecipientEmailAddress;
            setIsErrorModalOpen(true);
            setErrorInfo({
                title: t('LIVE_SEND_VIEWER_RECIPIENT_ERROR'),
                message: t('LIVE_SEND_VIEWER_RECIPIENT_OPT_OUT_ERROR', { recipient })
            });
            UxEventService.navigateViewEvent({ Context: UXContextType.SHARE_MODAL, Target: 'Recipient opt out error' });
        } else {
            setShowNotification(true);
            setShareSuccess(false);
            setIsOpenShareModal(false);
        }
    };

    const updateSelectPrivacyLink = (result) => {
        props.linkDispatch({ type: ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL, payload: result });
    };

    const viewerClassname = classnames('seismic-engagement-link-viewer', { 'customer-footer': hasCustomFooter, 'mobile-device': device.mobile() });

    return (
        <div className="seismic-engagement-viewer-container">
            {selectedContent.id && <Header
                validPasscode={validPasscode}
                openPanel={openPanel}
                setOpenPanel={setOpenPanel} />}
            <div className={viewerClassname}>
                <CompliancePage validPasscode={validPasscode}
                    setPasscodeValidation={setPasscodeValidation}
                    openPanel={openPanel}
                    setOpenPanel={setOpenPanel}
                    onSelectContent={handleSelectContent}>
                    {
                        (isLoadingManifest || !selectedManifest) ?
                            <div className="engagement-viewer-busy-spinner" /> :
                            <LinkViewer
                                key="linkViewer"
                                openPanel={openPanel}
                                setOpenPanel={setOpenPanel}
                                onSelectContent={handleSelectContent} />
                    }
                </CompliancePage>
            </div>
            {showShareModal && <ShareModal
                isOpen={showShareModal}
                setIsOpen={setIsOpenShareModal}
                updateSelectLink={updateSelectPrivacyLink}
                onShareSuccess={onShareSuccess}
                onShareFailed={onShareFailed} />}
            {showNotification && <Notification onDismiss={() => { setShowNotification(false); }} duration={10000} type={shareSuccess ? 'success' : 'failed'}>
                {shareSuccess ? <Icons.CheckOutlinedIcon className="share-success-icon" /> : <Icons.WarningIcon className="share-failed-icon" />}
                <span className="share-message">
                    {shareSuccess ? t('LIVE_SEND_VIEWER_LINK_SHARED') : t('LIVE_SEND_VIEWER_SHARE_FAILED')}
                </span>
                <Icons.CloseIcon className="close-notification-icon" onClick={() => { setShowNotification(false); }} />
            </Notification>}
            <ErrorModal
                className={device.mobile() ? 'mobile-device' : ''}
                isOpen={isErrorModalOpen}
                setIsOpen={setIsErrorModalOpen}
                highlightColor={viewerSetting.brandingHilightColor}
                errorInfo={errorInfo}
            />
        </div>
    );
};

export default withLinkStore(ViewerContainer);
